import type { JSX, RefObject } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { Calendar as CalendarIcon, X } from 'lucide-preact';
import dayjs from 'dayjs';
import { Popover } from '../overlays/Popover';
import { Calendar } from './Calendar';
import { cn } from '@/lib/cn';

export interface DatePickerProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  placeholder?: string;
  format?: string;
  min?: Date;
  max?: Date;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
  className?: string;
  /** id en el trigger — permite asociar un <label htmlFor> (ej. FormField). */
  id?: string;
  onBlur?: JSX.FocusEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
  /** Id del mensaje que describe el control (error o ayuda del FormField). */
  'aria-describedby'?: string;
}

const TRIGGER_BASE =
  'flex min-w-0 w-full items-center gap-2 rounded-md border bg-surface text-fg text-left transition-[border-color,box-shadow] duration-med ease-snappy focus-within:outline-none disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer';

const STATE: Record<'default' | 'error', string> = {
  default:
    'border-border hover:border-border-strong focus-within:border-brand focus-within:shadow-focus',
  error:
    'border-error hover:border-error focus-within:border-error focus-within:shadow-focus-error',
};

const SIZE: Record<NonNullable<DatePickerProps['size']>, string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-9 px-3 text-13',
  lg: 'h-10 px-3.5 text-sm',
};

export function DatePicker({
  value,
  onChange,
  placeholder = 'Selecciona una fecha',
  format = 'D MMM YYYY',
  min,
  max,
  size = 'md',
  disabled,
  invalid,
  clearable = true,
  className,
  id,
  onBlur,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: DatePickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const display = value ? dayjs(value).format(format) : null;
  const st = STATE[invalid ? 'error' : 'default'];

  const showClear = clearable && value && !disabled;

  return (
    <>
      {/* El botón de limpiar es un <button> HERMANO del trigger (absoluto), no
          anidado: un control interactivo no puede contener otro (axe
          `nested-interactive`, serious). className del consumidor → wrapper. */}
      <div className={cn('relative', className)}>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={invalid}
          onBlur={onBlur}
          onClick={() => setOpen((p) => !p)}
          className={cn(TRIGGER_BASE, SIZE[size], showClear && 'pr-9', st)}
        >
          <CalendarIcon size={14} className="text-fg-faint shrink-0" />
          <span className={cn('flex-1 truncate', !display && 'text-fg-faint')}>
            {display ?? placeholder}
          </span>
        </button>
        {showClear ? (
          <button
            type="button"
            aria-label="Limpiar fecha"
            className="text-fg-faint hover:bg-surface-2 hover:text-fg focus-visible:shadow-focus absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 transition-colors focus-visible:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          >
            <X size={12} />
          </button>
        ) : null}
      </div>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement="bottom-start"
      >
        <Calendar
          value={value}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
          min={min}
          max={max}
        />
      </Popover>
    </>
  );
}
